<template lang="pug">
	.flex-col.space-medium.full-height.app-core--list-view-link-unlink
		Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions') + '..'", :fullscreen="true")
		.link-unlink-actions.flex-row.align-center.space.justify
			.flex-row.align-center.space-medium
				slot(name="left-actions")
				DxButton(icon="plus", @click="activateSearch(true)", :title="searchHint", :disabled="!!isLoading")
			.flex-row.align-center.space-medium
				slot(name="right-actions")
		.flex-row.space.flex-1
			.flex-col.flex-1
				.font-small.grey.no-data(v-if="!linkedItems.length && !isLoading") {{$tCaption('Add any existing item or create new one')}}
				.flex-col.space
					.linked-item.flex-row.space.align-center(v-for="item in linkedItems")
						.flex-1(v-if="unlinkAlign !== 'left'")
							slot(name="item", :item="item")
						.linked-button.border.flex-col.align-center.center(@click="linkUnlink(item, false)", :title="$tCaption('Remove from list')")
							fas(icon="times")
						.flex-1(v-if="unlinkAlign === 'left'")
							slot(name="item", :item="item")
			.app-core--list-view-link-unlink.search-panel-wrapper(v-if="searchActivated", :class="{ active: searchAnimated }", @click="activateSearch(false)", ref="searchPanelWrapper",
				:style="{ '--searchPanelWidth': `${searchPanelWidth}px`}")
				.search-panel.flex-col.space(@click.stop="")
					ListView.full-width.no-padding(
						ref="list",
						initialView="cards",
						:getTotal="(r) => r.TotalNumberOfRecords",
						:getItems="(r) => r.Results",
						:getData="allItemsRequest",
						:filterRow="true",
						:remoteSorting="true",
						:hideViewsSelection="true",
						:autoFit="true",
						:heightDelta="0",
						:showColumnHeaders="false",
						:searchVisible="true",
						:topPagerVisible="false"
					)
						template(v-slot:left-actions="{ data }")
							.flex-row.align-center.space-medium
								DxButton(v-if="allowNew && saveAction", icon="plus", :text="$tCaption('New', '@actions')", @click="createItem", :hint="$tCaption('Create new item')")
								slot(name="search-left-actions", :data="data")
						template(v-slot:right-actions="{ data }")
							slot(name="search-right-actions", :data="data")
						template(v-slot:card="{ data }")
							.flex-row.space.align-center.item-template(@click="linkUnlink(data)")
								.flex-col.full-width.flex-1.searching-item
									slot(name="item", :searching="true", :item="data", :linked="isLinked(data)")
								.linked-button.flex-col.align-center.center(v-if="allowEdit && saveAction", @click.stop="edit(data)")
									fas(icon="pencil")
								.linked-button.linked-state.flex-col.align-center.center(:class="{ linked: isLinked(data) }")
									fas(icon="check")
		Popup(v-if="itemPopup.visible", :popupVisible="itemPopup.visible", :width="newItemPopupSizes[0]", :height="newItemPopupSizes[1]")
			.content(:class="{ 'disabled-controls': itemPopup.loading }")
				slot(name="item", :editing="true", :item="itemPopup.item", :events.sync="itemPopup.events")
				Loader(v-if="itemPopup.loading", text="", :fullscreen="true")
			template(slot="actions-left")
				DxButton(v-if="deleteAction", :text="$tCaption('Delete', '@actions')", type="danger", @click="deleteItem(itemPopup.item)", :disabled="!!itemPopup.loading")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="itemPopup.visible = false")
				DxButton(:text="$tCaption('Save', '@actions')", @click="saveItem", type="success", :disabled="!!itemPopup.loading")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import ListView from 'app-core/ListView.vue';
	import DxButton from 'devextreme-vue/button';
	import Popup from 'app-core/Popup.vue';
	import Loader from 'app-core/Loader.vue';

	export default {
		name: 'app-core--popup-body',
		props: {
			searchPanelWidth: VueTypes.number.def(380),
			allItemsRequest: VueTypes.any.def(null),
			linkedItemsRequest: VueTypes.any.def(null),
			saveAction: VueTypes.any.def(null),
			deleteAction: VueTypes.any.def(null),
			searchHint: VueTypes.any.def(null),
			newItemResolver: VueTypes.any.def(null),
			newItemPopupSizes: VueTypes.any.def([480,380]),
			collectionField: VueTypes.any.def(null),
			keyExpr: VueTypes.any.def('Oid'),
			allowEdit: VueTypes.any.def(true),
			allowNew: VueTypes.any.def(true),
			rootItemId: VueTypes.any.def(null),
			rootObjectTypeId: VueTypes.any.def(null),
			unlinkAlign: VueTypes.any.def('right')
		},
		components: {
			DxScrollView, ListView, DxButton, Popup, Loader
		},
		data() {
			return {
				linkedItems: [],
				searchActivated: false,
				searchAnimated: false,
				itemPopup: {
					visible: false,
					item: null,
					loading: false,
					events: {
						afterSave: null
					}
				},
				isLoading: false
			}
		},
		watch: {
			linkedItems(v) {
				this.$emit('linked-items', v);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.listViewLinkUnlink = this;
			this.loadLinkedItems();
		},
		destroyed() {
			if (this.$search) {
				document.body.removeChild(this.$search);
				delete this.$search;
			}
		},
		methods: {
			deleteItem(item) {
				if (this.deleteAction) {
					this.$confirm().then(() => {
						this.itemPopup.loading++;
						this.linkUnlink(item, false, true);
						this.deleteAction(item[this.keyExpr]).then(() => {
							if (this.$refs.list?.reload)
								this.$refs.list.reload();
							this.$notifySuccess(this.$tCaption('Item removed'));
						}).finally(() => {
							this.itemPopup.loading--;
							this.itemPopup.visible = false;
						});						
					})
				}
			},
			linkUnlink(item, forcedValue, silent) {
				let isLinked = this.isLinked(item);
				if (forcedValue !== void 0) {
					if (isLinked === forcedValue) return;
					isLinked = !forcedValue;
				}
				if (!isLinked) {
					this.linkedItems.push(item);
					this.$Data.linkUnlink(this.rootItemId, this.rootObjectTypeId, this.collectionField, [item[this.keyExpr]], true);
				} else {
					const unlink = () => {
						const idx = this.linkedItems.findIndex(f => f[this.keyExpr] === item[this.keyExpr]);
						if (idx >= 0) {
							this.linkedItems.splice(idx, 1);
						}
						this.$Data.linkUnlink(this.rootItemId, this.rootObjectTypeId, this.collectionField, [item[this.keyExpr]], false);
					}
					if (silent) {
						unlink();
					} else {
						this.$confirm(void 0, this.$tCaption('Unlink item?')).then(() => unlink());
					}
				}
			},
			edit(item) {
				this.itemPopup.item = item;
				this.itemPopup.visible = false;
				this.$nextTick(() => this.itemPopup.visible = true);
			},
			isLinked(item) {
				return false || this.linkedItems.some(f => f[this.keyExpr] === item[this.keyExpr]);
			},
			loadLinkedItems() {
				if (!this.linkedItemsRequest) return;
				this.isLoading++;
				this.linkedItemsRequest(0, 99, void 0, this.rootItemId).then(r => {
					this.linkedItems = r?.Results || r || [];
				}).finally(() => this.isLoading--);
			},
			activateSearch(v) {
				this.searchActivated = !v;
				this.searchAnimated = !v;
				
				setImmediate(() => {
					this.searchActivated = v;
					setImmediate(() => this.searchAnimated = v);

					if (!v) {
						if (this.$search) {
							document.body.removeChild(this.$search);
							delete this.$search;
						}
					} else {
						this.$nextTick(() => {
							this.$search = this.$refs.searchPanelWrapper;
							document.body.appendChild(this.$refs.searchPanelWrapper);
						});
					}
				});
			},
			createItem() {
				this.$set(this.itemPopup, 'item', this.newItemResolver && this.newItemResolver() || {});
				this.itemPopup.visible = false;
				this.$nextTick(() => this.itemPopup.visible = true);
			},
			updateLinkedItem(item) {
				const linkedItem = this.linkedItems.find(f => f[this.keyExpr] === item[this.keyExpr]);
				if (linkedItem) {
					_.assign(linkedItem, item);
				}
			},
			saveItem() {
				if (this.saveAction) {
					this.itemPopup.loading++;
					this.saveAction(this.itemPopup.item).then(async r => {
						const item = r?.Data;
						const isNew = !this.itemPopup.item[this.keyExpr];

						_.assign(this.itemPopup.item, item);
						if (this.itemPopup.events.afterSave) {
							await this.itemPopup.events.afterSave(this.itemPopup.item, { isNew, item });
						}

						if (isNew) {
							this.linkUnlink(this.itemPopup.item, true);
							if (this.$refs.list?.reload)
								this.$refs.list.reload();
						} else {
							this.updateLinkedItem(this.itemPopup.item);
						}

						this.itemPopup.visible = false;
						return item;
					}).finally(() => {
						this.itemPopup.loading--;
					});
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.app-core--list-view-link-unlink {
		position: relative;
		min-height: 320px;

		.searching-item {
			cursor: pointer;
		}

		.linked-button {
			width: 30px;
			height: 30px;
			border-radius: 40px;
			border: 1px solid transparent;
			cursor: pointer;

			&.border {
				border: 1px solid #ddd;
			}

			> * {
				color: #aaa;
			}

			&:hover {
				border: 1px solid $base-accent;

				> * {
					color: $base-accent;
				}
			}
		}

		.linked-state {
			border: 1px solid #ddd;
			transition: 0.2s;

			> * {
				transition: 0.2s;
				transform: scale(0);
			}
			&.linked, &:hover {
				border: 1px solid $base-accent;

				> * {
					color: $base-accent;
					transform: scale(1);
				}
			}
		}

		.item-template {
			&:hover {
				.linked-state {
					> * {
						transform: scale(1);
					}
				}
			}
		}

		.card {
			flex: auto !important;
			width: 100%;
			margin: 0px;
			padding: 10px 0px;
			border-bottom: 1px solid #eaeaea;
		}
	}

	.app-core--list-view-link-unlink.search-panel-wrapper {
			position: fixed;
			top: 0px;
			left: 0px;
			width: 100%;
			height: 100%;
			background: transparent;
			transition: 0.3s;
			z-index: 1501;

			.search-panel {
				height: 100%;
				right: -100%;
				position: absolute;
				width: var(--searchPanelWidth);
				max-width: 70%;
				background: #f8f8f8;
				box-shadow: 0 1px 3px rgba(0,0,0,.24);
				padding: 20px;
				transition: 0.3s;
			}

			&.active {
				background: rgba(0,0,0,.4);

				.search-panel {
					right: 0px;
				}
			}
			
		}
</style>
